import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Chart from "chart.js/auto";
import LodgeDetailsModal from "./LodgeDetailsModal";
import "./StatsLodges.css";

const exportCsv = (filename, headers, rows) => {
    const escape = (value) => {
        const text = String(value ?? "");
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const csv = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const statusColor = (status) => {
    const normal = String(status || "Unknown").toLowerCase();
    if (normal.includes("inactive")) return "rgba(148, 163, 184, 0.72)";
    if (normal.includes("active")) return "rgba(20, 184, 166, 0.74)";
    if (normal.includes("unknown")) return "rgba(129, 140, 248, 0.72)";
    return "rgba(244, 63, 94, 0.68)";
};

function StatsLodges({ onClose }) {
    const [statsData, setStatsData] = useState({ lodges: [], statuses: [], constitutions: [], timeline: [], location_history: [] });
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedConstitution, setSelectedConstitution] = useState("");
    const [coverageOnly, setCoverageOnly] = useState(false);
    const [editingLodgeId, setEditingLodgeId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const statusChartRef = useRef(null);
    const timelineChartRef = useRef(null);
    const coverageChartRef = useRef(null);
    const activityChartRef = useRef(null);
    const statusChart = useRef(null);
    const timelineChart = useRef(null);
    const coverageChart = useRef(null);
    const activityChart = useRef(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                setError(null);
                const { data } = await axios.get("/api/v1/stats/lodges");
                setStatsData(data || {});
            } catch (err) {
                console.error("Error fetching lodge stats:", err);
                setError("Failed to load lodge statistics.");
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    const lodges = useMemo(() => statsData.lodges || [], [statsData.lodges]);
    const filteredLodges = useMemo(() => {
        return lodges.filter((lodge) => {
            const hasCoverage = lodge.document_count || lodge.meeting_count || lodge.member_count || lodge.location_moves;
            return (!selectedStatus || lodge.status === selectedStatus) &&
                (!selectedConstitution || lodge.constitution === selectedConstitution) &&
                (!coverageOnly || hasCoverage);
        });
    }, [coverageOnly, lodges, selectedConstitution, selectedStatus]);

    const summary = useMemo(() => {
        const total = filteredLodges.length;
        const active = filteredLodges.filter((lodge) => String(lodge.status || "").toLowerCase() === "active").length;
        const inactive = filteredLodges.filter((lodge) => String(lodge.status || "").toLowerCase() === "inactive").length;
        const unknown = filteredLodges.filter((lodge) => String(lodge.status || "").toLowerCase() === "unknown").length;
        const docs = filteredLodges.reduce((sum, lodge) => sum + Number(lodge.document_count || 0), 0);
        const meetings = filteredLodges.reduce((sum, lodge) => sum + Number(lodge.meeting_count || 0), 0);
        const members = filteredLodges.reduce((sum, lodge) => sum + Number(lodge.member_count || 0), 0);
        const moves = filteredLodges.reduce((sum, lodge) => sum + Number(lodge.location_moves || 0), 0);
        return { total, active, inactive, unknown, docs, meetings, members, moves };
    }, [filteredLodges]);

    const statusRows = useMemo(() => {
        const counts = {};
        filteredLodges.forEach((lodge) => {
            const status = lodge.status || "Unknown";
            counts[status] = (counts[status] || 0) + 1;
        });
        return Object.entries(counts).map(([status, count]) => ({ status, count })).sort((a, b) => b.count - a.count);
    }, [filteredLodges]);

    const ageBandRows = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const bands = [
            { label: "Under 50", min: 0, max: 49 },
            { label: "50-99", min: 50, max: 99 },
            { label: "100-149", min: 100, max: 149 },
            { label: "150+", min: 150, max: 999 },
            { label: "Unknown", min: null, max: null },
        ];
        return bands.map((band) => ({
            label: band.label,
            count: filteredLodges.filter((lodge) => {
                if (!lodge.established_year) return band.label === "Unknown";
                const age = currentYear - lodge.established_year;
                return age >= band.min && age <= band.max;
            }).length,
        }));
    }, [filteredLodges]);

    const timelineRows = useMemo(() => {
        const years = filteredLodges
            .flatMap((lodge) => [lodge.established_year, lodge.decommissioned_year])
            .filter((year) => Number.isFinite(year));
        if (!years.length) return [];
        const minYear = Math.min(...years);
        const maxYear = Math.max(...years, new Date().getFullYear());
        let established = 0;
        let decommissioned = 0;
        const rows = [];
        for (let year = minYear; year <= maxYear; year++) {
            established += filteredLodges.filter((lodge) => lodge.established_year === year).length;
            decommissioned += filteredLodges.filter((lodge) => lodge.decommissioned_year === year).length;
            rows.push({ year, established, decommissioned, active_estimate: established - decommissioned });
        }
        return rows;
    }, [filteredLodges]);

    const topDocuments = useMemo(() => [...filteredLodges].sort((a, b) => b.document_count - a.document_count).slice(0, 12), [filteredLodges]);
    const topActivity = useMemo(() => [...filteredLodges]
        .map((lodge) => ({ ...lodge, activity_score: lodge.document_count + lodge.meeting_count * 3 + lodge.member_count * 2 + lodge.location_moves }))
        .sort((a, b) => b.activity_score - a.activity_score)
        .slice(0, 12), [filteredLodges]);
    const needsAttention = useMemo(() => filteredLodges
        .map((lodge) => ({
            ...lodge,
            missing: [
                !lodge.established_year && "founding year",
                !lodge.status || lodge.status === "Unknown" ? "status" : null,
                !lodge.document_count && "documents",
                !lodge.member_count && "members",
                !lodge.meeting_count && "meetings",
            ].filter(Boolean),
        }))
        .filter((lodge) => lodge.missing.length)
        .sort((a, b) => b.missing.length - a.missing.length || a.name.localeCompare(b.name)), [filteredLodges]);

    useEffect(() => {
        if (!statusChartRef.current || loading || error) return;
        if (statusChart.current) statusChart.current.destroy();
        statusChart.current = new Chart(statusChartRef.current.getContext("2d"), {
            type: "doughnut",
            data: {
                labels: statusRows.map((row) => row.status),
                datasets: [{
                    data: statusRows.map((row) => row.count),
                    backgroundColor: statusRows.map((row) => statusColor(row.status)),
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: "bottom" },
                    title: { display: true, text: "Lodges by Status" },
                },
            },
        });
        return () => statusChart.current?.destroy();
    }, [error, loading, statusRows]);

    useEffect(() => {
        if (!timelineChartRef.current || loading || error) return;
        if (timelineChart.current) timelineChart.current.destroy();
        timelineChart.current = new Chart(timelineChartRef.current.getContext("2d"), {
            type: "line",
            data: {
                labels: timelineRows.map((row) => row.year),
                datasets: [
                    {
                        label: "Established",
                        data: timelineRows.map((row) => row.established),
                        borderColor: "rgba(245, 158, 11, 1)",
                        backgroundColor: "rgba(245, 158, 11, 0.16)",
                        fill: true,
                        tension: 0.18,
                    },
                    {
                        label: "Decommissioned",
                        data: timelineRows.map((row) => row.decommissioned),
                        borderColor: "rgba(244, 63, 94, 1)",
                        backgroundColor: "rgba(244, 63, 94, 0.12)",
                        fill: true,
                        tension: 0.18,
                    },
                    {
                        label: "Active estimate",
                        data: timelineRows.map((row) => row.active_estimate),
                        borderColor: "rgba(20, 184, 166, 1)",
                        backgroundColor: "rgba(20, 184, 166, 0.13)",
                        fill: true,
                        tension: 0.18,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: "index", intersect: false },
                scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                plugins: {
                    legend: { position: "top" },
                    title: { display: true, text: "Lodge Growth Over Time" },
                },
            },
        });
        return () => timelineChart.current?.destroy();
    }, [error, loading, timelineRows]);

    useEffect(() => {
        if (!coverageChartRef.current || loading || error) return;
        if (coverageChart.current) coverageChart.current.destroy();
        coverageChart.current = new Chart(coverageChartRef.current.getContext("2d"), {
            type: "bar",
            data: {
                labels: topDocuments.map((lodge) => lodge.name),
                datasets: [{
                    label: "Documents",
                    data: topDocuments.map((lodge) => lodge.document_count),
                    backgroundColor: "rgba(37, 99, 235, 0.68)",
                }],
            },
            options: {
                indexAxis: "y",
                responsive: true,
                maintainAspectRatio: false,
                scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
                plugins: {
                    legend: { display: false },
                    title: { display: true, text: "Document Coverage by Lodge" },
                },
            },
        });
        return () => coverageChart.current?.destroy();
    }, [error, loading, topDocuments]);

    useEffect(() => {
        if (!activityChartRef.current || loading || error) return;
        if (activityChart.current) activityChart.current.destroy();
        activityChart.current = new Chart(activityChartRef.current.getContext("2d"), {
            type: "bar",
            data: {
                labels: ageBandRows.map((row) => row.label),
                datasets: [{
                    label: "Lodges",
                    data: ageBandRows.map((row) => row.count),
                    backgroundColor: ["#14b8a6", "#60a5fa", "#f59e0b", "#8b5cf6", "#94a3b8"],
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                plugins: {
                    legend: { display: false },
                    title: { display: true, text: "Lodges by Age Band" },
                },
            },
        });
        return () => activityChart.current?.destroy();
    }, [ageBandRows, error, loading]);

    const exportRegister = () => {
        exportCsv(
            "lodge-statistics-register.csv",
            ["Lodge", "Status", "Constitution", "Established", "Decommissioned", "Documents", "Meetings", "Members", "Location Moves", "Warrants"],
            filteredLodges.map((lodge) => [
                lodge.name,
                lodge.status,
                lodge.constitution,
                lodge.established_year || "",
                lodge.decommissioned_year || "",
                lodge.document_count,
                lodge.meeting_count,
                lodge.member_count,
                lodge.location_moves,
                lodge.warrant_count,
            ])
        );
    };

    const exportGaps = () => {
        exportCsv(
            "lodge-records-needing-attention.csv",
            ["Lodge", "Status", "Constitution", "Missing"],
            needsAttention.map((lodge) => [lodge.name, lodge.status, lodge.constitution, lodge.missing.join(", ")])
        );
    };

    const exportChart = (chart, filename) => {
        if (!chart.current) return;
        const link = document.createElement("a");
        link.href = chart.current.toBase64Image();
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    return (
        <div className="lodges-stats-overlay">
            <div className="lodges-stats-content">
                <header className="lodges-stats-header">
                    <div>
                        <p className="lstat-eyebrow">Lodge Intelligence</p>
                        <h2>Lodge Statistics</h2>
                    </div>
                    <button type="button" className="lstat-close-btn" onClick={onClose} aria-label="Close Lodge Statistics">&times;</button>
                </header>

                <div className="lodges-stats-body">
                    {loading && <div className="loading-spinner">Loading lodge stats...</div>}
                    {error && <div className="error-message">{error}</div>}

                    {!loading && !error && (
                        <>
                            <section className="lstat-toolbar">
                                <label>
                                    Status
                                    <select value={selectedStatus} onChange={(event) => setSelectedStatus(event.target.value)}>
                                        <option value="">All statuses</option>
                                        {(statsData.statuses || []).map((status) => <option key={status} value={status}>{status}</option>)}
                                    </select>
                                </label>
                                <label>
                                    Constitution
                                    <select value={selectedConstitution} onChange={(event) => setSelectedConstitution(event.target.value)}>
                                        <option value="">All constitutions</option>
                                        {(statsData.constitutions || []).map((constitution) => <option key={constitution} value={constitution}>{constitution}</option>)}
                                    </select>
                                </label>
                                <label className="lstat-check">
                                    <input type="checkbox" checked={coverageOnly} onChange={(event) => setCoverageOnly(event.target.checked)} />
                                    Coverage only
                                </label>
                                <button className="lstat-primary-btn" onClick={exportRegister}>Export Register</button>
                                <button className="lstat-secondary-btn" onClick={exportGaps}>Export Gaps</button>
                            </section>

                            <section className="lstat-kpi-grid">
                                <Kpi label="Lodges" value={summary.total} />
                                <Kpi label="Active" value={summary.active} />
                                <Kpi label="Inactive" value={summary.inactive} />
                                <Kpi label="Documents" value={summary.docs} />
                                <Kpi label="Meetings" value={summary.meetings} />
                                <Kpi label="Members Linked" value={summary.members} />
                                <Kpi label="Location Moves" value={summary.moves} />
                                <Kpi label="Need Attention" value={needsAttention.length} />
                            </section>

                            <section className="lstat-grid-two">
                                <Panel title="Status Mix" action={<button onClick={() => exportChart(statusChart, "lodge-status-mix.png")}>PNG</button>}>
                                    <div className="lstat-chart">
                                        <canvas ref={statusChartRef}></canvas>
                                    </div>
                                </Panel>
                                <Panel title="Age Profile" action={<button onClick={() => exportChart(activityChart, "lodge-age-profile.png")}>PNG</button>}>
                                    <div className="lstat-chart">
                                        <canvas ref={activityChartRef}></canvas>
                                    </div>
                                </Panel>
                            </section>

                            <Panel title="Lodge Growth Timeline" action={<button onClick={() => exportChart(timelineChart, "lodge-growth-timeline.png")}>PNG</button>}>
                                <div className="lstat-chart lstat-chart-wide">
                                    <canvas ref={timelineChartRef}></canvas>
                                </div>
                            </Panel>

                            <section className="lstat-grid-two">
                                <Panel title="Document Coverage" action={<button onClick={() => exportChart(coverageChart, "lodge-document-coverage.png")}>PNG</button>}>
                                    <div className="lstat-chart lstat-chart-tall">
                                        <canvas ref={coverageChartRef}></canvas>
                                    </div>
                                </Panel>
                                <Panel title="Archive Activity Ranking" action={<span>{topActivity.length} ranked</span>}>
                                    <StatsTable
                                        headers={["#", "Lodge", "Score", "Docs", "Meetings", "Members"]}
                                        rows={topActivity.map((lodge, index) => [
                                            index + 1,
                                            lodge.name,
                                            lodge.activity_score,
                                            lodge.document_count,
                                            lodge.meeting_count,
                                            lodge.member_count,
                                        ])}
                                        empty="No activity data."
                                    />
                                </Panel>
                            </section>

                            <section className="lstat-grid-two">
                            <Panel title="Records Needing Attention" action={<span>{needsAttention.length} records</span>}>
                                <StatsTable
                                    headers={["Lodge", "Status", "Missing", "Update"]}
                                    rows={needsAttention.slice(0, 30).map((lodge) => [
                                        lodge.name,
                                        lodge.status,
                                        lodge.missing.join(", "),
                                        <button className="lstat-link-btn" onClick={() => setEditingLodgeId(lodge.id)}>Update</button>,
                                    ])}
                                    empty="No gaps for this filter."
                                />
                            </Panel>
                                <Panel title="Recent Location History" action={<span>{(statsData.location_history || []).length} moves</span>}>
                                    <StatsTable
                                        headers={["Lodge", "Building", "Start", "End"]}
                                        rows={(statsData.location_history || []).slice(0, 30).map((row) => [
                                            row.lodge_name,
                                            row.building_name || "",
                                            row.start_date || "",
                                            row.end_date || "Present/unknown",
                                        ])}
                                        empty="No location history."
                                    />
                                </Panel>
                            </section>

                            <Panel title="Lodge Register" action={<span>{filteredLodges.length} shown</span>}>
                                <StatsTable
                                    headers={["Lodge", "Status", "Constitution", "Established", "Docs", "Meetings", "Members", "Moves"]}
                                    rows={filteredLodges.map((lodge) => [
                                        lodge.name,
                                        lodge.status,
                                        lodge.constitution,
                                        lodge.established_year || "",
                                        lodge.document_count,
                                        lodge.meeting_count,
                                        lodge.member_count,
                                        lodge.location_moves,
                                    ])}
                                    empty="No lodges match these filters."
                                />
                            </Panel>
                        </>
                    )}
                </div>
                {editingLodgeId && (
                    <LodgeDetailsModal
                        initialLodgeId={editingLodgeId}
                        onClose={() => setEditingLodgeId(null)}
                    />
                )}
            </div>
        </div>
    );
}

function Kpi({ label, value }) {
    return (
        <div className="lstat-kpi">
            <strong>{value}</strong>
            <span>{label}</span>
        </div>
    );
}

function Panel({ title, action, children }) {
    return (
        <section className="lstat-panel">
            <div className="lstat-panel-heading">
                <h3>{title}</h3>
                {action && <div className="lstat-panel-action">{action}</div>}
            </div>
            {children}
        </section>
    );
}

function StatsTable({ headers, rows, empty }) {
    return (
        <div className="lstat-table-wrap">
            <table className="lstat-table">
                <thead>
                <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
                </thead>
                <tbody>
                {rows.length ? rows.map((row, rowIndex) => (
                    <tr key={`${rowIndex}-${row[0]}`}>
                        {row.map((cell, cellIndex) => <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>)}
                    </tr>
                )) : (
                    <tr>
                        <td colSpan={headers.length}>{empty}</td>
                    </tr>
                )}
                </tbody>
            </table>
        </div>
    );
}

export default StatsLodges;
